import {useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {Plus} from 'lucide-react';
import {Collection} from '@tinct/web-collection';
import {useGetAgencyChildOrganizations} from '@tinct/core-api';
import {LayoutContent, LayoutHeader, LayoutTopbar} from '@tinct/web-layout';
import {useConnectedUser} from '@tinct/core-store-session';
import {useCurrentOrganization, useGetCurrentQuotas} from '@tinct/core-workspace';
import {Organization, User} from '@tinct/core-api-models';
import {Button} from '@tinct/web-components';
import {useUserOrganizationsColumns} from '../list-user-organizations-page/hooks/use-user-organizations-columns';
import {UpdateOrganizationModal} from '../list-user-organizations-page/modals/update-organization-modal';
import {DeleteOrganizationModal} from '../list-user-organizations-page/modals/delete-organization-modal';
import {CreateChildOrganizationModal} from '../../modals/create-child-organization-modal';

/**
 * Home of an agency workspace: the client workspaces it manages. Agencies hold
 * no content of their own, so this replaces the dashboard.
 */
export function ListAgencyWorkspacesPage() {
  const navigate = useNavigate();
  const connectedUser = useConnectedUser() as User;
  const userId = connectedUser?.id;
  const agency = useCurrentOrganization();
  const agencyId = agency.id ?? '';
  const {quotas} = useGetCurrentQuotas();
  const workspaceSeats = quotas?.find((q) => q.featureKey === 'workspace_count');

  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [renameModalOpen, setRenameModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedOrganization, setSelectedOrganization] = useState<Organization | null>(null);

  const columns = useUserOrganizationsColumns({
    onManageWorkspace: (organization) => {
      if (organization.id) navigate(`/w/${organization.id}/settings`);
    },
    onRenameWorkspace: (organization) => {
      setSelectedOrganization(organization);
      setRenameModalOpen(true);
    },
    onDeleteWorkspace: (organization) => {
      setSelectedOrganization(organization);
      setDeleteModalOpen(true);
    },
  });

  if (!userId) {
    return <div>Loading...</div>;
  }

  const seatsDescription = workspaceSeats
    ? ` ${workspaceSeats.currentUsage ?? 0}/${workspaceSeats.quotaLimit ?? '∞'} workspace seats used.`
    : '';

  return (
    <div>
      <header className="sticky top-0 z-30">
        <LayoutTopbar />
      </header>
      <LayoutContent>
        <LayoutHeader
          pageTitle="Client workspaces"
          pageDescription={`Workspaces managed by ${agency.name ?? 'your agency'}.${seatsDescription}`}
        />
        <div className="flex flex-col gap-6">
          <Collection
            useLocationAsState
            columns={columns}
            filtersConfiguration={{
              textFilter: {
                placeholder: 'Search workspaces by name',
                queryParamName: 'term',
              },
            }}
            toolbarActions={[
              <Button key="create-workspace" onClick={() => setCreateModalOpen(true)} className="cursor-pointer">
                <Plus className="h-4 w-4" />
                Create client workspace
              </Button>,
            ]}
            useQueryFn={useGetAgencyChildOrganizations}
            staticParams={{userId, agencyId}}
            queryFields={{
              id: null,
              name: null,
              type: null,
              createdAt: null,
              updatedAt: null,
              deleteAt: null,
              status: null,
              // organizationId/organization expose who owns the subscription: a child
              // covered by its agency's subscription gets a "via <agency>" chip.
              subscription: {
                fields: {
                  id: null,
                  status: null,
                  organizationId: null,
                  organization: {fields: {name: null}},
                  productGroup: {fields: {id: null, name: null}},
                  // The subscribed product(s), shown under the plan badge.
                  items: {
                    fields: {
                      id: null,
                      price: {fields: {id: null, nickname: null, product: {fields: {id: null, name: null}}}},
                    },
                  },
                },
              },
              snippetStatus: {fields: {status: null}},
              statistics: {
                fields: {
                  publishedCampaignsCount: null,
                  activeMembersCount: null,
                },
              },
            }}
          />
        </div>
      </LayoutContent>

      <CreateChildOrganizationModal
        open={createModalOpen}
        onOpenChange={setCreateModalOpen}
        agencyId={agencyId}
      />

      {selectedOrganization && (
        <>
          <UpdateOrganizationModal
            open={renameModalOpen}
            onOpenChange={setRenameModalOpen}
            organization={selectedOrganization}
          />
          <DeleteOrganizationModal
            open={deleteModalOpen}
            onOpenChange={setDeleteModalOpen}
            organization={selectedOrganization}
          />
        </>
      )}
    </div>
  );
}
